import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Container as ContainerBootstrap, Nav, Tab } from "react-bootstrap";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory } from "react-router";
import { useLocation } from "react-router-dom";
import { v4, validate } from "uuid";
import { withAuthorizationVerifier } from "../../../components/authorization-verifier";
import { useChainData } from "../../../components/chain-data-provider";
import ContentWrapper from "../../../components/content-wrapper";
import { FullWidthLoader } from "../../../components/full-width-loader";
import { withPropertyData } from "../../../components/property-admin-data-provider";
import { usePropertyLocales } from "../../../components/property-locale-provider";
import { useProperty } from "../../../components/property-provider";
import { SectionHeading } from "../../../components/section-heading";
import { ExtraAPIPromiseClient } from "../../../gen/witbooking/backoffice/extra/v1/extra_api_grpc_web_pb";
import {
  CreateExtraRequest,
  GetExtraRequest,
  SyncMultimediaRequest,
  UpdateExtraRequest,
} from "../../../gen/witbooking/backoffice/extra/v1/extra_api_pb";
import { useGrpcClient, usePropertyGrpcCall } from "../../../lib/grpc";
import { useExtraChangeGuard } from "../../extrasCategories/application/hooks/useExtraChangeGuard";
import { useExtrasCategoriesFlag } from "../../extrasCategories/application/hooks/useExtrasCategoriesFlag";
import { ExtraCategories, ExtraCategory } from "../../extrasCategories/domain/ExtraCategory.enum";
import { ValidationIssueMapper } from "../../extrasCategories/infrastructure/mappers/ValidationIssueMapper";
import {
  ExtraChangeContext,
  ExtraChangeDialogs,
  extraDisplayName,
} from "../../extrasCategories/presentation/components/ExtraImpactDialog";
import { useExtraCategoryIssues } from "../../extrasCategories/presentation/components/ExtraCategoryInputs";
import { useExtraSaveWarnings } from "../../extrasCategories/presentation/components/ExtraSaveWarnings";
import { SaveWarningsDialog } from "../../extrasCategories/presentation/components/SaveWarningsDialog";
import { Image } from "../../shared/widgets/ImageManager";
import { typeMessage } from "../messages";
import { ExtraType } from "../types";
import { defaultData } from "./defaultData";
import { useInventory } from "./hooks/inventory.hook";
import { useModal } from "./hooks/modal.hook";
import { useMultimedia } from "./hooks/multimedia.hook";
import {
  countPropagatedProperties,
  mapToGRPCBookableRefFromObject,
  mapToGRPCFromObject,
  mapToObjectFromGRPC,
} from "./mappers";
import { messages } from "./messages";
import { MultimediaTabPanel } from "./renders/MultimediaTabPanel";
import { OneTimePromoCodeTabPanel } from "./renders/OneTimePromoCodeTabPanel";
import { PropertiesTabPanel } from "./renders/PropertiesTabPanel";
import { StyledCard } from "./styles";
import { CRUD_MODE, CustomExtra, ExtraModalActionType, FormType } from "./types";

function EditExtra() {
  const location = useLocation();
  const property = useProperty();
  const history = useHistory();
  const allowedLocales = usePropertyLocales();
  const chainData = useChainData();
  const { handleUploadImages, handleUpdateImages } = useMultimedia();
  const { showModal } = useModal();
  const { allowedChainInventoryLinesTickers } = useInventory();
  const extrasCategoriesActive = useExtrasCategoriesFlag();
  const intl = useIntl();
  const {
    guard: guardExtraChange,
    showRejection: showExtraChangeRejection,
    dialogs: extraChangeDialogs,
  } = useExtraChangeGuard();
  const { showSaveWarnings, dialog: saveWarningsDialog } = useExtraSaveWarnings();

  const [mode, setMode] = useState<CRUD_MODE>();
  const [imageFiles, setImageFiles] = useState<Image[]>([]);
  const [extra, setExtra] = useState<CustomExtra>(defaultData);
  const [loading, setLoading] = useState(false);

  // GRPC
  const grpcClient = useGrpcClient(ExtraAPIPromiseClient);
  const getExtra = usePropertyGrpcCall(grpcClient, grpcClient.getExtra);
  const createExtra = usePropertyGrpcCall(grpcClient, grpcClient.createExtra);
  const updateExtra = usePropertyGrpcCall(grpcClient, grpcClient.updateExtra);
  const syncMultimedia = usePropertyGrpcCall(grpcClient, grpcClient.syncMultimedia);

  const isChain = useMemo(() => chainData?.isChain, [chainData]);
  const isFromAgr = useMemo(() => extra.ticker.endsWith("_agr"), [extra]);
  const isModification = useMemo(() => CRUD_MODE.EDIT === mode, [mode]);

  const methods = useForm<FormType>({
    mode: "onBlur",
    values: {
      id: defaultData.id,
      ticker: extra?.ticker || defaultData?.ticker,
      active: extra?.active ?? defaultData?.active,
      codesList: extra?.codesList?.join(",") || defaultData?.codesList.join(","),
      emailsList: extra?.emailsList?.join(",") || defaultData?.emailsList.join(","),
      inventoryLinesList: extra?.inventoryLinesList || defaultData?.inventoryLinesList,
      chainInventoryLinesList:
        extra?.chainInventoryLinesList || defaultData?.chainInventoryLinesList,
      translationsList: extra?.translationsList || defaultData?.translationsList,
      rateType: extra?.rateType || defaultData?.rateType,
      ratePrice: extra?.ratePrice || defaultData?.ratePrice,
      visible: extra?.visible ?? defaultData?.visible,
      type: extra?.type || defaultData?.type,
      availability: extra?.availability || defaultData?.availability,
      applyTo: extra?.applyTo || defaultData?.applyTo,
      applyToGuests: extra?.applyToGuests || defaultData?.applyToGuests,
      maxUnits: extra?.maxUnits || defaultData?.maxUnits,
      lockType: extra?.lockType || defaultData?.lockType,
      blockage: extra?.blockage || defaultData?.blockage,
      travelWindow: extra?.travelWindow || defaultData?.travelWindow,
      minStayType: extra?.minStayType || defaultData?.minStayType,
      minStay: extra?.minStay || defaultData?.minStay,
      maxStayType: extra?.maxStayType || defaultData?.maxStayType,
      maxStay: extra?.maxStay || defaultData?.maxStay,
      minNoticeType: extra?.minNoticeType || defaultData?.minNoticeType,
      minNotice: extra?.minNotice || defaultData?.minNotice,
      maxNoticeType: extra?.maxNoticeType || defaultData?.maxNoticeType,
      maxNotice: extra?.maxNotice || defaultData?.maxNotice,
      tax: extra?.tax || defaultData?.tax,
      category: extra?.category ?? defaultData.category,
      vehicleType: extra?.vehicleType ?? defaultData.vehicleType,
      requestLicensePlate: extra?.requestLicensePlate ?? defaultData.requestLicensePlate,
    },
  });

  const showCategoryIssues = useExtraCategoryIssues(methods.setError);

  /** A rejection with issues on the category fields keeps the user on the form. */
  const handleCategoryIssues = useCallback(
    (error: unknown, formValues: FormType) => {
      if (!extrasCategoriesActive || !showCategoryIssues(error, formValues.category)) {
        return false;
      }
      setLoading(false);
      return true;
    },
    [extrasCategoriesActive, showCategoryIssues]
  );

  /**
   * Context of the impact dialog; it adds the "category will be removed" sentence when the new type
   * no longer supports categories.
   */
  const extraChangeContext = useCallback(
    (formValues: FormType): ExtraChangeContext => ({
      extraName: extraDisplayName(formValues.translationsList, intl.locale, extra.ticker),
      typeChange:
        extra.category !== ExtraCategory.NONE && !ExtraCategories.supportsExtraType(formValues.type)
          ? { typeName: intl.formatMessage(typeMessage[formValues.type]) }
          : undefined,
    }),
    [extra.category, extra.ticker, intl]
  );

  const watchType = useWatch({ control: methods.control, name: "type" }) as ExtraType;

  const actionMessage = useMemo(() => {
    switch (mode) {
      case CRUD_MODE.EDIT:
        return isFromAgr ? messages.viewExtra : messages.editExtra;
      case CRUD_MODE.DUPLICATE:
        return messages.duplicateExtra;
      case CRUD_MODE.ADD:
      default:
        return messages.createExtra;
    }
  }, [isFromAgr, mode]);

  /**
   * Back handler
   */

  const handleOnBack = useCallback(() => {
    history.push(`/${property.ticker}/extras`);
  }, [history, property.ticker]);

  /**
   * Action handlers
   */

  const handleSyncMultimedia = useCallback(
    (extraId: string) => {
      if (!isChain) return;
      const request = new SyncMultimediaRequest();
      request.setExtraId(extraId);
      return syncMultimedia(request);
    },
    [isChain, syncMultimedia]
  );

  const handleGetOne = useCallback(
    async (id: string): Promise<CustomExtra> => {
      const request = new GetExtraRequest();
      request.setId(id);
      const response = await getExtra(request);

      const chainInventoryLinesList = Object.values(
        response.toObject().appliesToAgrInventoriesMap
      ).map(([childPropertyTicker, { tickersList }]) => ({
        [childPropertyTicker]: tickersList,
      }));

      const sortedChainInventoryLinesList = chainInventoryLinesList
        .sort((a, b) => {
          const [firstKey] = Object.keys(a);
          const [secondKey] = Object.keys(b);
          return (
            allowedChainInventoryLinesTickers.indexOf(firstKey) -
            allowedChainInventoryLinesTickers.indexOf(secondKey)
          );
        })
        .map(it => Object.values(it).flat());

      return {
        ...mapToObjectFromGRPC(response.getExtra()?.toObject()!!, allowedLocales),
        chainInventoryLinesList: sortedChainInventoryLinesList,
      };
    },
    [allowedChainInventoryLinesTickers, allowedLocales, getExtra]
  );

  const handleCreate = useCallback(
    async (formValues: FormType) => {
      setLoading(true);
      try {
        const extraData = { ...formValues, id: v4(), ticker: `e_${formValues.ticker}` };
        const values = mapToGRPCFromObject(extraData, imageFiles, {
          withCategorization: extrasCategoriesActive,
        });
        const request = new CreateExtraRequest();
        request.setExtra(values);

        if (formValues.chainInventoryLinesList.length > 0) {
          allowedChainInventoryLinesTickers.forEach((ticker, index) => {
            const inventoryList = formValues.chainInventoryLinesList[index];
            if (inventoryList) {
              request
                .getAppliesToAgrInventoriesMap()
                .set(ticker, mapToGRPCBookableRefFromObject(inventoryList));
            }
          });
        }

        const response = await createExtra(request);
        await handleUploadImages(extraData, imageFiles);
        await handleSyncMultimedia(extraData.id);
        await showSaveWarnings({
          categoriesActive: extrasCategoriesActive,
          category: formValues.category,
          propagatedTo: countPropagatedProperties(request.getAppliesToAgrInventoriesMap()),
          warnings: ValidationIssueMapper.toDomainList(response.getWarningsList()),
        });
        await showModal({ type: ExtraModalActionType.SUCCESS_CREATE }, handleOnBack);
      } catch (e) {
        console.error(e);
        if (handleCategoryIssues(e, formValues)) return;
        await showModal({ type: ExtraModalActionType.ERROR_CREATE }, handleOnBack);
      }
    },
    [
      allowedChainInventoryLinesTickers,
      createExtra,
      extrasCategoriesActive,
      handleCategoryIssues,
      handleOnBack,
      handleSyncMultimedia,
      handleUploadImages,
      imageFiles,
      showModal,
      showSaveWarnings,
    ]
  );

  const handleUpdate = useCallback(
    async (formValues: FormType) => {
      setLoading(true);
      try {
        const request = new UpdateExtraRequest();
        const values = mapToGRPCFromObject(formValues, imageFiles, {
          withCategorization: extrasCategoriesActive,
        });
        request.setExtraId(extra.id);
        request.setExtra(values);

        if (formValues.chainInventoryLinesList.length > 0) {
          allowedChainInventoryLinesTickers.forEach((ticker, index) => {
            const inventoryList = formValues.chainInventoryLinesList[index];
            if (inventoryList) {
              request
                .getAppliesToAgrInventoriesMap()
                .set(ticker, mapToGRPCBookableRefFromObject(inventoryList));
            }
          });
        }

        // The impact check gets exactly the request that UpdateExtra receives.
        if (
          (await guardExtraChange({ update: request }, extraChangeContext(formValues))) === "abort"
        ) {
          setLoading(false);
          return;
        }

        const result = await updateExtra(request);
        await handleUpdateImages(extra, formValues, imageFiles);
        await handleSyncMultimedia(extra.id);
        await showSaveWarnings({
          categoriesActive: extrasCategoriesActive,
          category: formValues.category,
          propagatedTo: countPropagatedProperties(request.getAppliesToAgrInventoriesMap()),
          warnings: ValidationIssueMapper.toDomainList(result.getWarningsList()),
        });
        await showModal(
          {
            type: ExtraModalActionType.SUCCESS_UPDATE,
            data: Object.fromEntries(result.toObject().dataMap),
          },
          handleOnBack
        );
      } catch (e) {
        console.error(e);
        // Blocked on the server because it would leave a limits block empty (concurrent change, or
        // flag off on an AGR parent).
        if (showExtraChangeRejection(e, extraChangeContext(formValues))) {
          setLoading(false);
          return;
        }
        if (handleCategoryIssues(e, formValues)) return;
        await showModal({ type: ExtraModalActionType.ERROR_UPDATE }, handleOnBack);
      }
    },
    [
      allowedChainInventoryLinesTickers,
      extra,
      extraChangeContext,
      extrasCategoriesActive,
      guardExtraChange,
      handleCategoryIssues,
      handleOnBack,
      handleSyncMultimedia,
      handleUpdateImages,
      imageFiles,
      showExtraChangeRejection,
      showModal,
      showSaveWarnings,
      updateExtra,
    ]
  );

  const handlerAction = useCallback(
    (formValues: FormType) => {
      switch (mode) {
        case CRUD_MODE.EDIT:
          void handleUpdate(formValues);
          break;
        case CRUD_MODE.ADD:
        case CRUD_MODE.DUPLICATE:
        default:
          void handleCreate(formValues);
          break;
      }
    },
    [handleCreate, handleUpdate, mode]
  );

  /**
   * Effects
   */

  useEffect(() => {
    const id = location.pathname.split("/").pop();
    if (id && validate(id)) {
      const [action] = location.pathname.split("/").slice(-2, -1);
      switch (action) {
        case "extras-copy":
          setMode(CRUD_MODE.DUPLICATE);
          return void handleGetOne(id)
            .then((extra: CustomExtra) => setExtra({ ...extra, ticker: "" }))
            .catch(async () => {
              await showModal({ type: ExtraModalActionType.ERROR_GET_ONE }, handleOnBack);
            })
            .finally(() => setLoading(false));
        case "extras-edit":
        default:
          setMode(CRUD_MODE.EDIT);
          return void handleGetOne(id)
            .then((extra: CustomExtra) => setExtra(extra))
            .catch(async () => {
              await showModal({ type: ExtraModalActionType.ERROR_GET_ONE }, handleOnBack);
            })
            .finally(() => setLoading(false));
      }
    }
    return setMode(CRUD_MODE.ADD);
  }, [handleGetOne, handleOnBack, location.pathname, showModal]);

  useEffect(() => {
    if (extra.multimediaList.length > 0) {
      setImageFiles([...extra.multimediaList]);
    }
  }, [extra]);

  useEffect(() => {
    if (watchType && watchType !== extra.type) {
      methods.setValue("availability", defaultData.availability);
      methods.setValue("applyTo", defaultData.applyTo);
      methods.setValue("blockage", defaultData.blockage);
      methods.setValue("lockType", defaultData.lockType);
    }
  }, [extra, methods, watchType]);

  if (loading) {
    // The guard and save warnings dialogs open while the save is in progress.
    return (
      <>
        <FullWidthLoader />
        <ExtraChangeDialogs {...extraChangeDialogs} />
        <SaveWarningsDialog {...saveWarningsDialog} />
      </>
    );
  }

  return (
    <ContentWrapper>
      <FormProvider {...methods}>
        <SectionHeading>
          <FormattedMessage {...actionMessage} />
        </SectionHeading>
        <ContainerBootstrap fluid={true}>
          <Tab.Container defaultActiveKey="properties">
            <StyledCard>
              <Card.Header>
                <Nav variant="tabs">
                  <Nav.Item key="properties">
                    <Nav.Link eventKey="properties">
                      <FormattedMessage {...messages.properties} />
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item key="images">
                    <Nav.Link eventKey="images">
                      <FormattedMessage {...messages.images} />
                    </Nav.Link>
                  </Nav.Item>
                  {isModification && (
                    <Nav.Item key="one-time-promo-code">
                      <Nav.Link eventKey="oneTimePromoCode">
                        <FormattedMessage {...messages.oneTimePromoCode} />
                      </Nav.Link>
                    </Nav.Item>
                  )}
                </Nav>
              </Card.Header>

              <Card.Body>
                <Tab.Content>
                  <PropertiesTabPanel
                    extra={extra}
                    handleOnBack={handleOnBack}
                    handlerAction={handlerAction}
                    isFromAgr={isFromAgr}
                    methods={methods}
                    mode={mode}
                  />
                </Tab.Content>

                <Tab.Content>
                  <MultimediaTabPanel
                    extra={extra}
                    handleOnBack={handleOnBack}
                    handlerAction={handlerAction}
                    isFromAgr={isFromAgr}
                    methods={methods}
                    setImageFiles={setImageFiles}
                  />
                </Tab.Content>

                {isModification && (
                  <Tab.Content>
                    <OneTimePromoCodeTabPanel
                      extra={extra}
                      isFromAgr={isFromAgr}
                      allowedChainInventoryLinesTickers={allowedChainInventoryLinesTickers}
                      handleOnBack={handleOnBack}
                    />
                  </Tab.Content>
                )}
              </Card.Body>
            </StyledCard>
          </Tab.Container>
        </ContainerBootstrap>
      </FormProvider>
      <ExtraChangeDialogs {...extraChangeDialogs} />
      <SaveWarningsDialog {...saveWarningsDialog} />
    </ContentWrapper>
  );
}

export default withAuthorizationVerifier(withPropertyData(EditExtra));
